import { Imatge } from '@portal-internet/components';
import { getStorageValue, setStorageValue } from '@portal-internet/core';
import Boto from 'components/3cat/ui/Boto';
import Icona from 'components/3cat/ui/Icona';
import { useModulAvis } from 'hooks/useModulAvis';
import { useProducte } from 'hooks/3cat/useProducte';
import { useUser } from 'hooks/useUser';
import __isUndefined from 'lodash/isUndefined';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import styles from './onboardingenviupoc.module.scss';

// --- HELPER D'ADOBE TARGET PER CLICS ---
// Aquesta funció envia un esdeveniment de clic a un mbox específic, passant-hi la variant.
const trackTargetClick = (clickMbox, variant) => {
    const trackEvent = globalThis?.window?.adobe?.target?.trackEvent;

    if (typeof trackEvent === 'function') {
        console.log(`🎯 Adobe Target: Trackejant clic a [${clickMbox}] - Variant [${variant}]`);

        trackEvent({
            mbox: clickMbox,
            params: {
                variant: variant || 'unknown',
                elementTag: 'button',
                elementText: 'Compra les entrades' // Opcional, però útil per analítica
            },
            success: () => {
                console.log('✅ Adobe Target: Clic enviat correctament');
            },
            error: (status, error) => {
                console.warn('❌ Adobe Target: Error enviant el clic', status, error);
            }
        });
    } else {
        console.warn('⚠️ Adobe Target: La funció trackEvent no està disponible');
    }
};
// ------------------------------

// --- HELPER D'ADOBE TARGET ---
// Aquesta funció espera a que Adobe Target estigui llest i demana l'oferta.
const fetchTargetVariant = async (mbox, fallbackVariant = 'A') => {
    const timeout = 3000; // Esperem màxim 3 segons a que carregui AT
    const interval = 100;
    const start = Date.now();
    let target = null;

    // 1. Esperem a que window.adobe.target estigui disponible
    while (Date.now() - start < timeout) {
        target = globalThis?.window?.adobe?.target;
        if (target) break;
        await new Promise((r) => setTimeout(r, interval));
    }

    if (!target) return fallbackVariant;

    // 2. Demanem l'oferta de forma estratègica
    return new Promise((resolve) => {
        try {
            if (typeof target.getOffer === 'function') {
                target.getOffer({
                    mbox,
                    success: (offer) => {
                        // 1. Busquem primer la versió dins l'estructura JSON: offer[0].content[0].version
                        let variantRaw = offer?.[0]?.content?.[0]?.version;

                        // 2. Normalitzem (ara variantRaw sí que serà el text 'B', 'C', etc.)
                        const variant = typeof variantRaw === 'string' ? variantRaw.trim().toUpperCase() : fallbackVariant;

                        resolve(variant);
                    },
                    error: (error) => {
                        console.warn('Error obtenint Adobe Target Offer', error);
                        resolve(fallbackVariant);
                    }
                });
            } else {
                resolve(fallbackVariant);
            }
        } catch (error) {
            console.warn('Error obtenint Adobe Target Offer', error);
            resolve(fallbackVariant);
        }
    });
};
// ------------------------------

export default function Component(props) {
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showedToday, setShowedToday] = useState(true);
    const [cookieChecked, setCookieChecked] = useState(false);

    // NOU ESTAT PER LA VARIANT DE TARGET
    const [targetVariant, setTargetVariant] = useState(null);
    const initialFetchDone = useRef(false);

    const timeoutShowOnboarding = useRef();
    const { isWebviewActive } = useProducte();
    const { isModulAvisActive } = useModulAvis();

    const router = useRouter();
    const refOnboarding = useRef();
    const isTest = typeof window !== 'undefined' && window.Cypress;

    const { signUp, signIn, loginState, loginStateTypes } = useUser();
    const nomCookie = 'onboarding';
    const estructuraCookieDefault = {
        day: '2020/01/01',
        variant: null // Afegim això per saber quin experiment som
    };

    const isEmbedOrPopup = router.asPath.includes('/popup/') || router.asPath.includes('/embed/');

    const saveOnStorage = (values) => {
        setStorageValue(nomCookie, values, {
            path: '/',
            domain: '.3cat.cat',
            expires: 1
        });
    };
    const initializeValues = (data) => {
        let response;
        if (__isUndefined(data)) {
            response = estructuraCookieDefault;
        } else {
            response = data;
            //if (__isUndefined(response.count)) {
            //    response.count = 0;
            //}
        }
        return response;
    };

    const fetchDayCookieData = async () => {
        const data = await getStorageValue(nomCookie);
        let value = initializeValues(data);
        let todayDate = getToday();

        // NOMÉS entrem aquí si NO s'ha mostrat avui
        if (value.day !== todayDate) {
            // 1. CRIDA A TARGET: Demanem l'oferta abans de pintar res
            const variant = await fetchTargetVariant('3cat-enviu-mbox', 'I');
            setTargetVariant(variant);

            // LOG per saber-ho al moment
            console.log(`🎯 Adobe Target [3cat-enviu-mbox]: Assignat a la Variant [${variant}]`);

            // 2. Actualitzem la cookie per assegurar-nos que demà ja no es torni a demanar
            // Guardem també la variant
            value.variant = variant;
            value.day = todayDate;

            setShowedToday(false);
            saveOnStorage(value);
        } else {
            // Si la cookie ja diu que s'ha vist avui, bloquegem tot i NO cridem a Target
            setShowedToday(true);

            // Recuperem la variant de la cookie per si la necessitem per analítica
            if (value.variant) {
                setTargetVariant(value.variant);
                console.log(`🎯 Adobe Target: Usuari prèviament assignat a la Variant [${value.variant}]`);
            } else {
                setTargetVariant('A'); // Fallback de seguretat
            }
        }
        setCookieChecked(true); // Marquem que ja hem verificat el procés
    };

    const tancaOnboarding = () => {
        netejeUrlOnboarding();
        setShowOnboarding(false);
        setShowedToday(true);
        document.getElementById('__mainContent').removeAttribute('inert');
    };

    const netejeUrlOnboarding = () => {
        let url = router.asPath?.split('#')[0];
        router.push(url, undefined, { scroll: false });
    };

    const getToday = () => {
        const dateObj = new Date();
        const month = dateObj.getUTCMonth() + 1;
        const day = dateObj.getUTCDate();
        const year = dateObj.getUTCFullYear();
        return year + '/' + month + '/' + day;
    };

    useEffect(() => {
        if (!initialFetchDone.current) {
            fetchDayCookieData();
            initialFetchDone.current = true; // Marquem que ja hem entrat
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!__isUndefined(refOnboarding.current) && showOnboarding === true && !isTest && !showedToday) {
            refOnboarding.current.focus();
            document.getElementById('__mainContent').setAttribute('inert', true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refOnboarding.current, showOnboarding]);

    useEffect(() => {
        if (
            // logats i no logats
            //loginState === loginStateTypes.GUEST &&
            !isWebviewActive &&
            cookieChecked &&
            !showedToday &&
            targetVariant !== null // Ens assegurem de no pintar fins tenir la variant
        ) {
            timeoutShowOnboarding.current = setTimeout(() => {
                setShowOnboarding(true);
            }, 1500);
        }
        return () => clearTimeout(timeoutShowOnboarding.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginState, cookieChecked, targetVariant, showedToday]);

    useEffect(() => {
        // Només volem afegir el listener si el modal s'està mostrant i el DOM existeix
        if (showOnboarding && refOnboarding.current) {
            // Busquem l'element <a> a dins del modal utilitzant la classe dinàmica del module.scss
            const botoCompra = refOnboarding.current.querySelector(`.${styles.btn_compra}`);

            if (botoCompra) {
                // Definim què farà el clic
                const handleTrackingClick = () => {
                    trackTargetClick('3cat-enviu-mbox-click', targetVariant);
                };

                // Hi enganxem el listener de Javascript pur
                botoCompra.addEventListener('click', handleTrackingClick);

                // CRÍTIC: Funció de neteja per evitar que s'acumulin múltiples listeners
                // si el component es torna a renderitzar.
                return () => {
                    botoCompra.removeEventListener('click', handleTrackingClick);
                };
            }
        }
    }, [showOnboarding, targetVariant]); // S'executarà quan s'obri el modal o canviï la variant

    const contentOnBoarding = () => {
        // En base al 'targetVariant' ('A', 'B', 'C', 'D'), retornem un disseny o un altre
        switch (targetVariant) {
            case 'B':
                return (
                    <div
                        className={styles.onBoardingEnViuPoC}
                        {...{
                            'role': 'dialog',
                            'aria-modal': 'true',
                            'aria-labelledby': 'onboarding-titol'
                        }}
                    >
                        <div
                            className={styles.mask + ' ' + styles.onboarding}
                            onClick={() => tancaOnboarding()}
                            ref={refOnboarding}
                        >
                            <div className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.img}>
                                    <div className={styles.imgTop}>
                                        <Imatge
                                            src={'https://img.3cat.cat/multimedia/png/9/8/1776258190989.png'}
                                            layout='fill'
                                            objectFit='cover'
                                            alt='El foraster'
                                        />
                                    </div>
                                </div>
                                <div className={styles.text}>
                                    <div className={styles.imgBottom}>
                                        <Imatge
                                            //src={'https://img.3cat.cat/multimedia/png/6/2/1776260169326.png'}
                                            src={'https://img.3cat.cat/multimedia/png/3/7/1776263840273.png'}
                                            layout='fill'
                                            objectFit='cover'
                                            alt='El foraster'
                                        />
                                    </div>
                                    <button className={styles.tanca} onClick={() => tancaOnboarding()}>
                                        <Icona icona='tanca' className={styles.ico_tanca} alt='Tanca' ariaHidden={true} />
                                        <span className='sr-only'>Tanca finestra modal</span>
                                    </button>

                                    <div className={styles.contingutMobil}>
                                        <h1 className={styles.title}>
                                            Edició especial (Variant B) <span className='sr-only'>El foraster</span>
                                        </h1>
                                        <div className={styles.wrapperLogo}>
                                            <Imatge
                                                src={'https://img.3cat.cat/multimedia/png/6/1/1767696224916_1920.png'}
                                                width={212}
                                                height={59}
                                                //objectFit='contain'
                                                alt='Logo El foraster'
                                            />
                                        </div>

                                        <p className={styles.subtitle}>
                                            <strong>
                                                DISSABTE 30 DE MAIG
                                                <br />A les 12.00 al Palau Sant Jordi
                                            </strong>
                                        </p>
                                        <p className={styles.subtitle}>
                                            Quim Masferrer reuneix els protagonistes més emblemàtics dels 120 pobles que ha
                                            visitat.
                                        </p>
                                        <div className={styles.btns}>
                                            <Boto
                                                link='https://www.3cat.cat/enviu/el-foraster/'
                                                tag='a'
                                                className={`${styles.btn_compra} ${styles.link}`}
                                                text='Compra les entrades'
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'C':
                return (
                    <div
                        className={styles.onBoardingEnViuPoC}
                        {...{
                            'role': 'dialog',
                            'aria-modal': 'true',
                            'aria-labelledby': 'onboarding-titol'
                        }}
                    >
                        <div
                            className={styles.mask + ' ' + styles.onboarding}
                            onClick={() => tancaOnboarding()}
                            ref={refOnboarding}
                        >
                            <div className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.img}>
                                    <div className={styles.imgTop}>
                                        <Imatge
                                            src={'https://img.3cat.cat/multimedia/png/9/8/1776258190989.png'}
                                            layout='fill'
                                            objectFit='cover'
                                            alt='El foraster'
                                        />
                                    </div>
                                </div>
                                <div className={styles.text}>
                                    <div className={styles.imgBottom}>
                                        <Imatge
                                            //src={'https://img.3cat.cat/multimedia/png/6/2/1776260169326.png'}
                                            src={'https://img.3cat.cat/multimedia/png/3/7/1776263840273.png'}
                                            layout='fill'
                                            objectFit='cover'
                                            alt='El foraster'
                                        />
                                    </div>
                                    <button className={styles.tanca} onClick={() => tancaOnboarding()}>
                                        <Icona icona='tanca' className={styles.ico_tanca} alt='Tanca' ariaHidden={true} />
                                        <span className='sr-only'>Tanca finestra modal</span>
                                    </button>

                                    <div className={styles.contingutMobil}>
                                        <h1 className={styles.title}>
                                            Edició especial (Variant C) <span className='sr-only'>El foraster</span>
                                        </h1>
                                        <div className={styles.wrapperLogo}>
                                            <Imatge
                                                src={'https://img.3cat.cat/multimedia/png/6/1/1767696224916_1920.png'}
                                                width={212}
                                                height={59}
                                                //objectFit='contain'
                                                alt='Logo El foraster'
                                            />
                                        </div>

                                        <p className={styles.subtitle}>
                                            <strong>
                                                DISSABTE 30 DE MAIG
                                                <br />A les 12.00 al Palau Sant Jordi
                                            </strong>
                                        </p>
                                        <p className={styles.subtitle}>
                                            Quim Masferrer reuneix els protagonistes més emblemàtics dels 120 pobles que ha
                                            visitat.
                                        </p>
                                        <div className={styles.btns}>
                                            <Boto
                                                link='https://www.3cat.cat/enviu/el-foraster/'
                                                tag='a'
                                                className={`${styles.btn_compra} ${styles.link}`}
                                                text='Compra les entrades'
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'D':
                return (
                    <div
                        className={styles.onBoardingEnViuPoC}
                        {...{
                            'role': 'dialog',
                            'aria-modal': 'true',
                            'aria-labelledby': 'onboarding-titol'
                        }}
                    >
                        <div
                            className={styles.mask + ' ' + styles.onboarding}
                            onClick={() => tancaOnboarding()}
                            ref={refOnboarding}
                        >
                            <div className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.img}>
                                    <div className={styles.imgTop}>
                                        <Imatge
                                            src={'https://img.3cat.cat/multimedia/png/9/8/1776258190989.png'}
                                            layout='fill'
                                            objectFit='cover'
                                            alt='El foraster'
                                        />
                                    </div>
                                </div>
                                <div className={styles.text}>
                                    <div className={styles.imgBottom}>
                                        <Imatge
                                            //src={'https://img.3cat.cat/multimedia/png/6/2/1776260169326.png'}
                                            src={'https://img.3cat.cat/multimedia/png/3/7/1776263840273.png'}
                                            layout='fill'
                                            objectFit='cover'
                                            alt='El foraster'
                                        />
                                    </div>
                                    <button className={styles.tanca} onClick={() => tancaOnboarding()}>
                                        <Icona icona='tanca' className={styles.ico_tanca} alt='Tanca' ariaHidden={true} />
                                        <span className='sr-only'>Tanca finestra modal</span>
                                    </button>

                                    <div className={styles.contingutMobil}>
                                        <h1 className={styles.title}>
                                            Edició especial (Variant D) <span className='sr-only'>El foraster</span>
                                        </h1>
                                        <div className={styles.wrapperLogo}>
                                            <Imatge
                                                src={'https://img.3cat.cat/multimedia/png/6/1/1767696224916_1920.png'}
                                                width={212}
                                                height={59}
                                                //objectFit='contain'
                                                alt='Logo El foraster'
                                            />
                                        </div>

                                        <p className={styles.subtitle}>
                                            <strong>
                                                DISSABTE 30 DE MAIG
                                                <br />A les 12.00 al Palau Sant Jordi
                                            </strong>
                                        </p>
                                        <p className={styles.subtitle}>
                                            Quim Masferrer reuneix els protagonistes més emblemàtics dels 120 pobles que ha
                                            visitat.
                                        </p>
                                        <div className={styles.btns}>
                                            <Boto
                                                link='https://www.3cat.cat/enviu/el-foraster/'
                                                tag='a'
                                                className={`${styles.btn_compra} ${styles.link}`}
                                                text='Compra les entrades'
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'A':
                // El disseny ORIGINAL (Fallback o Variant A)
                return (
                    <div
                        className={styles.onBoardingEnViuPoC}
                        {...{
                            'role': 'dialog',
                            'aria-modal': 'true',
                            'aria-labelledby': 'onboarding-titol'
                        }}
                    >
                        <div
                            className={styles.mask + ' ' + styles.onboarding}
                            onClick={() => tancaOnboarding()}
                            ref={refOnboarding}
                        >
                            <div className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.img}>
                                    <div className={styles.imgTop}>
                                        <Imatge
                                            src={'https://img.3cat.cat/multimedia/png/9/8/1776258190989.png'}
                                            layout='fill'
                                            objectFit='cover'
                                            alt='El foraster'
                                        />
                                    </div>
                                </div>
                                <div className={styles.text}>
                                    <div className={styles.imgBottom}>
                                        <Imatge
                                            //src={'https://img.3cat.cat/multimedia/png/6/2/1776260169326.png'}
                                            src={'https://img.3cat.cat/multimedia/png/3/7/1776263840273.png'}
                                            layout='fill'
                                            objectFit='cover'
                                            alt='El foraster'
                                        />
                                    </div>
                                    <button className={styles.tanca} onClick={() => tancaOnboarding()}>
                                        <Icona icona='tanca' className={styles.ico_tanca} alt='Tanca' ariaHidden={true} />
                                        <span className='sr-only'>Tanca finestra modal</span>
                                    </button>

                                    <div className={styles.contingutMobil}>
                                        <h1 className={styles.title}>
                                            Edició especial (Variant A) <span className='sr-only'>El foraster</span>
                                        </h1>
                                        <div className={styles.wrapperLogo}>
                                            <Imatge
                                                src={'https://img.3cat.cat/multimedia/png/6/1/1767696224916_1920.png'}
                                                width={212}
                                                height={59}
                                                //objectFit='contain'
                                                alt='Logo El foraster'
                                            />
                                        </div>

                                        <p className={styles.subtitle}>
                                            <strong>
                                                DISSABTE 30 DE MAIG
                                                <br />A les 12.00 al Palau Sant Jordi
                                            </strong>
                                        </p>
                                        <p className={styles.subtitle}>
                                            Quim Masferrer reuneix els protagonistes més emblemàtics dels 120 pobles que ha
                                            visitat.
                                        </p>
                                        <div className={styles.btns}>
                                            <Boto
                                                link='https://www.3cat.cat/enviu/el-foraster/'
                                                tag='a'
                                                className={`${styles.btn_compra} ${styles.link}`}
                                                text='Compra les entrades'
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
        }
    };

    // Validació final per pintar-ho tot (incloent que Target ja ens hagi respòs i no sigui null)
    if (showOnboarding && !isTest && !showedToday && !isEmbedOrPopup && !isModulAvisActive && targetVariant !== null) {
        return contentOnBoarding();
    } else {
        return null;
    }
}
